import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY!);
const organisationId = localStorage.getItem('organisationId') || '';

interface AddPaymentMethodFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  isFirstCard: boolean;
}

const AddPaymentMethodFormInner: React.FC<AddPaymentMethodFormProps> = ({
  onSuccess,
  onCancel,
  isFirstCard,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    const organisationId = localStorage.getItem('organisationId');
    if (!organisationId) {
      setError('No organisation selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const setupResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/organisations/${organisationId}/stripe/payment-methods/setup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            organisationId,
            paymentMethodTypes: ['card'],
            usage: 'off_session',
          }),
        }
      );

      if (!setupResponse.ok) {
        const errorData = await setupResponse.json();
        throw new Error(errorData.message || 'Failed to create setup intent');
      }

      const { setupIntent } = await setupResponse.json();

      // Get card element
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        throw new Error('Card element not found');
      }

      // Confirm setup with Stripe
      const { error: stripeError, setupIntent: confirmedIntent } = await stripe.confirmCardSetup(
        setupIntent.clientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      // Add payment method to account - set as default if it's the first card
      const addResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/organisations/${organisationId}/stripe/payment-methods/add`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({
            organisationId,
            paymentMethodId: confirmedIntent!.payment_method,
            setAsDefault: isFirstCard,
          }),
        }
      );

      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.message || 'Failed to add payment method');
      }

      if (onSuccess) onSuccess();
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 3, backgroundColor: '#fff' }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Add New Payment Method</Typography>
          <IconButton size="small" onClick={onCancel}>
            <CloseIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isFirstCard && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This will be set as your primary payment method since it's your first card.
          </Alert>
        )}

        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            mb: 3,
          }}
        >
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
            }}
          />
        </Box>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {onCancel && (
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !stripe}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Add Card
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Wrapper component that provides Stripe Elements context
const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = (props) => {
  return (
    <Elements stripe={stripePromise}>
      <AddPaymentMethodFormInner {...props} />
    </Elements>
  );
};

export default AddPaymentMethodForm;
